import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {mergeRegister} from '@lexical/utils';
import {$isDynamicNode} from 'components/DSYS/editor/nodes/DynamicNode';
import {$isTypeaheadNode} from 'components/DSYS/editor/nodes/TypeaheadNode';
import {
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_CRITICAL,
  DELETE_CHARACTER_COMMAND,
  KEY_BACKSPACE_COMMAND,
  LexicalEditor,
  LexicalNode,
  REMOVE_TEXT_COMMAND,
} from 'lexical';
import {useIsTemplateMode} from 'plugins/enhancedHeadings/RoleContext';
import {useEffect} from 'react';

function $isGuardedInlineNode(node: LexicalNode | null | undefined): boolean {
  return $isDynamicNode(node) || $isTypeaheadNode(node);
}

/**
 * When the caret is immediately after an inline dynamic/typeahead node, Lexical's default
 * backspace removes that node. In document (non-template) mode those nodes should stay
 * unless removed via template editing or explicit UI.
 */
const $guardedInlineNodeBehindCaret = (): LexicalNode | null => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
    return null;
  }

  const anchor = selection.anchor;
  const anchorNode = anchor.getNode();

  if (anchor.type === 'text' && $isTextNode(anchorNode)) {
    if (anchor.offset > 0) {
      return null;
    }
    const prev = anchorNode.getPreviousSibling();
    return $isGuardedInlineNode(prev) ? prev : null;
  }

  if (anchor.type === 'element' && $isElementNode(anchorNode)) {
    const idx = anchor.offset;
    if (idx === 0) {
      return null;
    }
    const childBefore = anchorNode.getChildAtIndex(idx - 1);
    return $isGuardedInlineNode(childBefore) ? childBefore : null;
  }

  return null;
};

function $shouldBlockGuardedDeletionInDocumentMode(): boolean {
  const selection = $getSelection();
  if ($isNodeSelection(selection)) {
    return selection.getNodes().some((n) => $isGuardedInlineNode(n));
  }
  return $guardedInlineNodeBehindCaret() !== null;
}

export function registerDynamicNodeDeletionGuard(editor: LexicalEditor): () => void {
  const blockIfNeeded = (): boolean => $shouldBlockGuardedDeletionInDocumentMode();

  return mergeRegister(
    editor.registerCommand(KEY_BACKSPACE_COMMAND, blockIfNeeded, COMMAND_PRIORITY_CRITICAL),
    editor.registerCommand(REMOVE_TEXT_COMMAND, blockIfNeeded, COMMAND_PRIORITY_CRITICAL),
    editor.registerCommand(
      DELETE_CHARACTER_COMMAND,
      (payload: boolean) => (payload ? blockIfNeeded() : false),
      COMMAND_PRIORITY_CRITICAL
    )
  );
}

export const DynamicNodeDeletionGuardPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const isTemplateMode = useIsTemplateMode();

  useEffect(() => {
    if (isTemplateMode) {
      return;
    }
    return registerDynamicNodeDeletionGuard(editor);
  }, [editor, isTemplateMode]);

  return null;
};
